import * as angular from 'angular';
import * as template from './template.html';
import './styling.scss';
import {
    getFormattedGeneralDeductibleAmount,
    getFormattedHurricaneDeductibleAmount,
    getFormattedWindDeductibleAmount
} from 'common/source/utilities/policyInfoDisplay';
import {
    getHurricaneDeductibleAmount,
    getWindDeductibleAmount,
    isWindCoverageExcluded
} from 'common/source/utilities/policyInfoHelper';

const module = angular.module('coverage-my-deductibles-section', []);

interface DeductibleDisplay extends NameAndValue {
    tooltipContent: string;
}

interface CoverageMyDeductiblesSectionController extends angular.IStrictController {
    title: string;
    icon: string;
    description: string;
    deductibles: Array<DeductibleDisplay>;
    showGeneralDeductibleDisclaimer: boolean;
    policyInfo: CustomerPolicyInfo;
    setDeductibles(): void;
}

module.component('coverageMyDeductiblesSection', {
    template: template,
    bindings: {
        policyInfo: '<'
    },
    controller: function(this: CoverageMyDeductiblesSectionController) {
        'ngInject';

        this.setDeductibles = () => {
            const deductibleTooltipContent = `Applies to eligible claims that are not a direct result of wind, hail or
                a named hurricane. Also known as "All Peril Deductible".`;
            const hurricaneDeductibleTooltipContent = `Applies to eligible claims that are the direct result of a named
                hurricane at the time the loss occurred.`;
            const windOrHailDeductibleTooltipContent = `Applies to eligible claims that are the direct result of
                wind or hail damage.`;
            const windExclusionTooltipContent = 'Coverage for hail or wind losses is not provided';

            const policyInfo = this.policyInfo;

            const formattedGeneralDeductibleAmount = getFormattedGeneralDeductibleAmount(policyInfo) || '';
            const windDeductibleAmount = getWindDeductibleAmount(policyInfo);
            const formattedWindDeductibleAmount = getFormattedWindDeductibleAmount(policyInfo) || '';
            const hurricaneDeductibleAmount = getHurricaneDeductibleAmount(policyInfo);
            const formattedHurricaneDeductibleAmount = getFormattedHurricaneDeductibleAmount(policyInfo) || '';
            const hasOnlySingleDeductible = !formattedWindDeductibleAmount && !formattedHurricaneDeductibleAmount;
            const hasWindExclusion = isWindCoverageExcluded(policyInfo);
            const coverageExcludedCopy = 'Cov. Excluded';

            if (formattedGeneralDeductibleAmount) {
                const hasOnlyGeneralDeductible = hasOnlySingleDeductible && !hasWindExclusion;

                if (hasOnlyGeneralDeductible) {
                    this.deductibles.push({
                        name: '',
                        value: formattedGeneralDeductibleAmount,
                        tooltipContent: ''
                    });
                } else {
                    this.deductibles.push({
                        name: 'General Deductible',
                        value: formattedGeneralDeductibleAmount,
                        tooltipContent: deductibleTooltipContent
                    });
                }
            }

            if (hasWindExclusion) {
                // TODO: support only wind excluded (without hurricane even offered in state)
                this.deductibles.push({
                    name: 'Hurricane Deductible',
                    value: coverageExcludedCopy,
                    tooltipContent: windExclusionTooltipContent
                });

                this.deductibles.push({
                    name: 'Wind or Hail Deductible',
                    value: coverageExcludedCopy,
                    tooltipContent: windExclusionTooltipContent
                });
            } else {
                if (hurricaneDeductibleAmount !== null) {
                    let hurricaneDeductibleValue;

                    if (hurricaneDeductibleAmount === 0) {
                        this.showGeneralDeductibleDisclaimer = true;
                        hurricaneDeductibleValue = formattedGeneralDeductibleAmount + '*';
                    } else {
                        hurricaneDeductibleValue = formattedHurricaneDeductibleAmount;
                    }

                    this.deductibles.push({
                        name: 'Hurricane Deductible',
                        value: hurricaneDeductibleValue,
                        tooltipContent: hurricaneDeductibleTooltipContent
                    });
                }

                if (windDeductibleAmount !== null) {
                    let windDeductibleValue;

                    if (windDeductibleAmount === 0) {
                        this.showGeneralDeductibleDisclaimer = true;
                        windDeductibleValue = formattedGeneralDeductibleAmount + '*';
                    } else {
                        windDeductibleValue = formattedWindDeductibleAmount;
                    }

                    this.deductibles.push({
                        name: 'Wind or Hail Deductible',
                        value: windDeductibleValue,
                        tooltipContent: windOrHailDeductibleTooltipContent
                    });
                }
            }
        };

        this.$onInit = () => {
            this.icon = 'green-money';
            this.description = `This is the amount of money you'll be required to pay for an eligible claim before
                your insurance plan starts to pay.`;
            this.deductibles = [];

            this.setDeductibles();

            this.title = this.deductibles.length === 1 ? 'My Deductible' : 'My Deductibles';
        };
    }
});

export { module };
